import React, { useContext, useMemo, useState } from 'react';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import { StockLocationRoleAssignmentContext } from '../../../../../context/StockLocationRoleAssignmentContext';
import { TagRelationContext } from '../../../../../context/TagRelationContext';
import {
  StockLocationRoleAssignmentMutation,
  UpdateStockLocationRoleAssignmentResponse,
  UpdateStockLocationRoleAssignmentVariables,
} from '../../../../../graphql/stockLocationRoleAssignment.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { useToast } from '../../../../../providers/ToastProvider';
import { CompanyRoleAssignment } from '../../../../../types/company_role_assignment/companyRoleAssignment';
import {
  StockLocationRoleAssignment,
  StockLocationRole,
  StockLocationRoleAssignmentUtil,
  StockLocationRoleAssignmentInput,
} from '../../../../../types/stock_location_role_assignment';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { User } from '../../../../../types/user/user';
import { toMap } from '../../../../../util/map.util';
import { StockLocationRoleAssignmentFilter } from '../../../../../VentoryUI/components/filters/StockLocationRoleAssignmentFilter/StockLocationRoleAssignmentFilter';
import { Button, ButtonVariant } from '../../../../components/Button';
import { TableDropdown } from '../../../../components/Dropdown';
import { SearchFilter } from '../../../../components/Filter';
import Table from '../../../../components/Table';
import { ToastType } from '../../../../components/Toast';
import { DeleteIconButton } from '../../../../icons';
import CreateStockLocationRoleForUserModal from './CreateStockLocationRoleForUserModal';
import DeleteStockLocationRolesForUserModal from './DeleteStockLocationRolesForUserModal';
import { CompanyContext } from '../../../../../context/CompanyContext';

interface UserStockLocationRolesViewProps {
  user: User;
  companyRole: CompanyRoleAssignment;
}

export default function UserStockLocationRolesView({ user, companyRole }: UserStockLocationRolesViewProps) {
  const { showToast } = useToast();

  const { stockLocationRoles, setStockLocationRoles } = useContext(StockLocationRoleAssignmentContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { currentCompany } = useContext(CompanyContext);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalProps, setDeleteModalProps] = useState({
    open: false,
    assignments: new Map<string, StockLocationRoleAssignment>(),
  });

  const userStockLocationRoles = useMemo(() => {
    return toMap(
      [...stockLocationRoles.values()].flat().filter(role => role.userId === companyRole.userId),
      'stockLocationId',
    );
  }, [stockLocationRoles]);

  const allItems = useMemo(() => [...userStockLocationRoles.values()], [userStockLocationRoles]);

  const [items, setItems] = useState(allItems);

  const [updateRoleAssignment, { loading }] = useMutation<
    UpdateStockLocationRoleAssignmentResponse,
    UpdateStockLocationRoleAssignmentVariables
  >(StockLocationRoleAssignmentMutation.update, {
    onCompleted: response => {
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.stockLocationRoleUpdatedSuccessfullyToastTitle).singular.upper,
      });
      const updatedStockLocationRole = new StockLocationRoleAssignment(response.updateStockLocationRoleAssignment[0]);
      const existing = stockLocationRoles.get(updatedStockLocationRole.stockLocationId) || [];
      stockLocationRoles.set(
        updatedStockLocationRole.stockLocationId,
        existing.map(slr => (slr.id === updatedStockLocationRole.id ? updatedStockLocationRole : slr)),
      );
      setStockLocationRoles(new Map(stockLocationRoles));
    },
  });

  const filter = new StockLocationRoleAssignmentFilter(tagRelations, stockLocations, currentCompany);

  return (
    <>
      {createModalOpen && (
        <CreateStockLocationRoleForUserModal
          user={user}
          userStockLocationRoles={userStockLocationRoles}
          open={createModalOpen}
          setOpen={setCreateModalOpen}
        />
      )}
      {deleteModalProps.open && deleteModalProps.assignments.size ? (
        <DeleteStockLocationRolesForUserModal
          assignments={deleteModalProps.assignments}
          open={deleteModalProps.open}
          setOpen={open => setDeleteModalProps({ ...deleteModalProps, open })}
          user={user}
        />
      ) : null}

      <div className='flex flex-grow flex-col gap-4'>
        <p className='section-title'>{t(TranslationKey.stockLocation).plural.upper}</p>
        <div className='flex items-center justify-between gap-4'>
          <SearchFilter items={allItems} setItems={setItems} filter={filter} />
          <Button
            variant={ButtonVariant.primary}
            text={t(TranslationKey.assignToLocation).singular.upper}
            onClick={() => setCreateModalOpen(true)}
          />
        </div>
        <Table
          items={items}
          filter={filter}
          headers={[
            {
              key: 'stock_location',
              name: t(TranslationKey.stockLocation).singular.upper,
              text: item =>
                stockLocations.get(item.stockLocationId)?.name || t(TranslationKey.unknownStockLocation).singular.upper,
            },
            {
              key: 'role',
              name: t(TranslationKey.role).singular.upper,
              text: item => {
                return (
                  <div className='w-full py-2'>
                    <TableDropdown
                      items={Object.values(StockLocationRole)}
                      value={item.role}
                      textBuilder={StockLocationRoleAssignmentUtil.roleToString}
                      onSelect={role => {
                        updateRoleAssignment({
                          variables: {
                            stockLocationRoleAssignments: [
                              new StockLocationRoleAssignmentInput(item).withRole(role).forUpdate(),
                            ],
                          },
                        });
                      }}
                    />
                  </div>
                );
              },
            },
            {
              key: 'delete',
              name: '',
              text: item => (
                <DeleteIconButton
                  onClick={() => setDeleteModalProps({ open: true, assignments: new Map().set(item.id, item) })}
                />
              ),
            },
          ]}
        />
      </div>
    </>
  );
}
